import MapService from 'common/services/Map';
import ApiRequests from 'common/services/ApiRequests';
import TrackingService from 'common/services/Tracking/Tracking';

/**
 * Recherche et géolocalisation.
 *
 * La maquette n'en contient rien : ses champs et ses boutons n'ont aucun
 * gestionnaire. Tout ce qui suit est écrit à partir du chemin déjà emprunté par
 * 2025_VOG — MapService.goToGeolocation() puis ApiRequests.getBrands() —, la
 * seule route client du storelocator.
 *
 * Chaque fonction renvoie `{ state, origin }` : `origin` porte l'information
 * dont la puce de distance a besoin (décision 9) et que l'API ne renvoie pas.
 */

/** Nombre de salons ramenés par recherche — aligné sur MapService.numberOfMarkers. */
const RESULTS_PER_PAGE = 20;

const paginationFor = (lat, lng) => ({
  skip: '0',
  number_of: RESULTS_PER_PAGE,
  order_by: 'distance',
  order_dir: 'asc',
  from_lat: lat,
  from_lng: lng,
});

/** Géolocalisation navigateur → salons les plus proches. */
export const searchAroundUser = async (state) => {
  const geoInfos = await MapService.goToGeolocation();
  if (!geoInfos) return null;

  const { from_lat: lat, from_lng: lng } = geoInfos.pagination;
  const nextState = await ApiRequests.getBrands(geoInfos.pagination, state);

  // Le libellé de la ville sert au fil d'ariane et aux métadonnées.
  let label = '';
  try {
    const reverse = await MapService.geoapifyService.getReverseGeocoding(lat, lng);
    if (reverse.length) {
      label = reverse[0].city || reverse[0].formatted || '';
      if (nextState.sl) nextState.sl.autocompleteData = reverse[0];
    }
  } catch (error) {
    console.warn('Géocodage inverse indisponible', error);
  }

  return {
    state: nextState,
    origin: { kind: 'user', label, lat, lng },
    placeId: geoInfos.placeId,
  };
};

/** Saisie libre (ville, code postal, adresse) → salons les plus proches. */
export const searchByPlaceName = async (state, query) => {
  const trimmed = (query || '').trim();
  if (!trimmed) return null;

  const places = await ApiRequests.geoapify.getPlaceByName(trimmed);
  if (!places || !places.length) return { state, origin: null, notFound: true };

  const place = places[0];
  const lat = place.lat;
  const lng = place.lon;

  try {
    TrackingService.trackSearchedPlace();
  } catch (error) {
    console.warn('Tracking de la recherche indisponible', error);
  }

  const nextState = await ApiRequests.getBrands(paginationFor(lat, lng), state);
  if (nextState.sl) nextState.sl.autocompleteData = place;

  return {
    state: nextState,
    origin: { kind: 'city', label: place.city || place.formatted || trimmed, lat, lng },
  };
};

/**
 * Prédiction Geoapify choisie dans le dropdown de `SearchField` → salons les
 * plus proches. `MapService.geoapifyService.goToLocationByPrediction` piste
 * déjà la recherche, pas besoin de le refaire ici.
 */
export const searchByPrediction = async (state, prediction) => {
  if (!prediction) return null;

  const { pagination } = await MapService.geoapifyService.goToLocationByPrediction(prediction);
  const nextState = await ApiRequests.getBrands(pagination, state);
  if (nextState.sl) nextState.sl.autocompleteData = prediction;

  return {
    state: nextState,
    origin: {
      kind: 'city',
      label: prediction.city || prediction.formatted,
      lat: prediction.lat,
      lng: prediction.lon,
    },
  };
};

export default { searchAroundUser, searchByPlaceName, searchByPrediction };
